import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Modal,
  IconButton,
  Fade,
  Backdrop,
} from "@mui/material";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

// Assets & Components
import Logo from "../../assets/centrum_assets/design/index/LOGO.png"; // Adjust path if needed
import DOMPurify from "dompurify";
import RichTextContent from "./RichTextContext"; // Adjust path if needed

const StationGallery = ({ images = [], quote = "", stationName = "Station" }) => {
  const [openModal, setOpenModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const modalSwiperRef = useRef(null);

  const handleOpenModal = (image) => {
    const index = images.indexOf(image);
    setCurrentImageIndex(index !== -1 ? index : 0);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleNextImage = (e) => {
    e?.stopPropagation();
    modalSwiperRef.current?.slideNext();
  };

  const handlePrevImage = (e) => {
    e?.stopPropagation();
    modalSwiperRef.current?.slidePrev();
  };

  return (
    <>
      {/* --- THUMBNAIL SWIPER (ON PAGE) --- */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, mt: 4, maxWidth: 1200, mx: "auto" }}>
        Station Gallery
      </Typography>

      <Box sx={{ width: "100%", maxWidth: { xs: "100%", sm: '100%', md: "72%", lg: '75%' }, ml: 0 }}>
        <Swiper
          modules={[Pagination]}
          spaceBetween={20}
          slidesPerView={3}
          loop={false}
          centerInsufficientSlides={true}
          pagination={{ clickable: true }}
          style={{ width: "100%", paddingBottom: "40px" }}
          breakpoints={{
            1200: { slidesPerView: 3 },
            900: { slidesPerView: 3 },
            600: { slidesPerView: 3 },
            0: { slidesPerView: 2 },
          }}
        >
          {images.length > 0 ? (
            images.map((slide, index) => (
              <SwiperSlide key={index}>
                <Box
                  sx={{
                    width: "100%",
                    position: "relative",
                    paddingTop: "100%",
                    overflow: "hidden",
                    cursor: "pointer",
                    backgroundColor: "#eee",
                    "&:hover img": {
                      transform: "scale(1.05)",
                      transition: "transform 0.3s ease"
                    }
                  }}
                  onClick={() => handleOpenModal(slide)}
                >
                  <img
                    src={slide}
                    alt={`${stationName} gallery ${index + 1}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.3s ease"
                    }}
                  />
                </Box>
              </SwiperSlide>
            ))
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>No gallery images available.</Typography>
          )}
        </Swiper>
      </Box>

      {/* --- FULLSCREEN LIGHTBOX MODAL --- */}
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 450 } }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1300
        }}
      >
        <Fade in={openModal} timeout={450}>
          <Box sx={{
            width: '100vw',
            height: '100vh',
            outline: 'none',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>

            {/* CLOSE BUTTON */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                top: 20,
                right: 20,
                zIndex: 200,
                color: "white",
                backgroundColor: "rgba(0,0,0,0.3)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" }
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>

            {/* ARROWS (Visible on Mobile & Desktop) */}
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: "absolute",
                left: { xs: 5, md: 20 },
                zIndex: 200,
                color: "white",
                backgroundColor: "rgba(0,0,0,0.3)",
                width: { xs: 40, md: 60 },
                height: { xs: 40, md: 60 },
                display: { xs: 'none', md: 'flex' },
                "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" }
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: { xs: 24, md: 35 } }} />
            </IconButton>

            <IconButton
              onClick={handleNextImage}
              sx={{
                position: "absolute",
                right: { xs: 5, md: 20 },
                zIndex: 200,
                color: "white",
                backgroundColor: "rgba(0,0,0,0.3)",
                width: { xs: 40, md: 60 },
                height: { xs: 40, md: 60 },
                display: { xs: 'none', md: 'flex' },
                "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" }
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: { xs: 24, md: 35 } }} />
            </IconButton>

            {/* SWIPER IN MODAL */}
            <Swiper
              modules={[Navigation, Pagination]}
              onBeforeInit={(swiper) => { modalSwiperRef.current = swiper; }}
              initialSlide={currentImageIndex}
              loop={true}
              speed={700}
              spaceBetween={50}
              slidesPerView={1}
              grabCursor={true}
              pagination={{ clickable: true }}
              style={{
                width: '100%',
                height: '100%',
                "--swiper-pagination-color": "#fff",
                "--swiper-pagination-bullet-inactive-color": "rgba(255,255,255, 0.5)",
                "--swiper-pagination-bullet-size": "16px",
                "--swiper-pagination-bullet-horizontal-gap": "6px"
              }}
            >
              {images.map((img, index) => (
                <SwiperSlide
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%'
                  }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget) handleCloseModal();
                  }}
                >
                  {/* THE CARD */}
                  <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      width: "100%",
                      maxWidth: "1200px",
                      backgroundColor: "white",
                      borderRadius: 2,
                      boxShadow: 24,
                      overflow: 'hidden',
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      maxHeight: { xs: '85vh', md: '90vh' },
                      mx: { xs: 2, md: 4 },
                      userSelect: "none",
                      WebkitUserSelect: "none",
                      touchAction: "pan-y",
                    }}
                  >
                    {/* IMAGE SIDE */}
                    <Box sx={{
                      flex: { xs: 'none', md: 3 },
                      height: { xs: 'auto', md: 'auto' },
                      position: 'relative',
                      backgroundColor: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      width: '100%'
                    }}>
                      <img
                        src={img}
                        alt={`${stationName} gallery ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "cover",
                          display: "block",
                          userSelect: "none",
                          WebkitUserDrag: "none"
                        }}
                      />
                    </Box>

                    {/* TEXT SIDE */}
                    <Box sx={{
                      flex: 1,
                      backgroundColor: "white",
                      p: { xs: 2, md: 4 },
                      display: "flex",
                      flexDirection: "column",
                      borderLeft: { md: '1px solid #eee' },
                      overflowY: "auto",
                      minWidth: { md: "300px" }
                    }}>
                      <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#123464', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                        Caption
                      </Typography>

                      <RichTextContent
                        component="div"
                        sx={{ textAlign: 'justify', maxWidth: '100%', mt: 2, '& p': { margin: 0, marginBottom: '1em', fontSize: { md: '1.4rem', xs: '1.7rem' }, fontWeight: 400, lineHeight: 1.7 } }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(quote || "No description available.") }}
                      />

                      <Box sx={{ mt: "auto", textAlign: "right" }}>
                        <img src={Logo} alt="Logo" style={{ width: "80px", height: "auto" }} />
                      </Box>
                    </Box>
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default StationGallery;